import {
  createStyles,
  withStyles,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Spinner,
  Typography,
  Grid,
  useTranslation
} from '@paintscout/ui'
import type { MuiTheme as Theme, StyleClasses, DialogProps } from '@paintscout/ui'
import { gql } from 'apollo-boost'
import { useApolloClient } from '@apollo/react-hooks'
// import FileSaver from 'file-saver'
import type { OptionsDocument, QuoteDocument } from 'paintscout'
import React, { useEffect, useState } from 'react'
import { getTemplatedFilename, getPdfBucketUrl } from '@paintscout/util/builder'
import { urlExists } from '@paintscout/util/builder/util/url-exists/url-exists'
import type { QuoteView } from '../../Quote'
import http from '@paintscout/util/http'
import { waitForPdf } from './waitForPdf'

export interface QuotePdfDialog extends Omit<DialogProps, 'message' | 'title' | 'loading'> {
  classes?: DialogProps['classes'] & StyleClasses<typeof styles>
  quote: QuoteDocument
  companyId: string
  quoteRev?: string
  options?: OptionsDocument
  silent?: boolean
  revision?: boolean
  view?: QuoteView
  custViewUrl?: string
  revisionQuoteUrl?: string
  paymentId?: string
  lang?: string
  onConfirm?: () => void
}

const styles = (theme: Theme) =>
  createStyles({
    root: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      width: '100%'
    },
    input: {
      minWidth: 300,
      [theme.breakpoints.up('md')]: {
        minWidth: 350
      }
    }
  })

function QuotePdfDialog({
  open,
  quote,
  companyId,
  options,
  classes,
  onConfirm,
  quoteRev = quote._rev,
  paymentId = null,
  silent = false,
  revision = false,
  view = 'quote',
  custViewUrl = null,
  revisionQuoteUrl = null,
  lang,
  ...props
}: QuotePdfDialog) {
  const [error, setError] = useState<{ error: unknown; message?: string }>(null)
  // The sync render gave up and the async task took over — we're polling the cache bucket for it.
  const [waiting, setWaiting] = useState(false)
  const apolloClient = useApolloClient()
  const { __ } = useTranslation()
  // Stops the poll when the dialog is cancelled or unmounted.
  const abortRef = React.useRef<AbortController | null>(null)

  useEffect(() => {
    async function fetchData() {
      try {
        const { quotePdfUrl, quoteViewUrl, quotePdfFilename } = await getPdfUrl(quote, quoteRev, companyId)
        // Sub in our huge urlDoc for testing

        // Check if url exists before proceeding
        const exists = await urlExists(quotePdfUrl)
        if (exists) {
          await downloadPdf(quotePdfUrl, quotePdfFilename)
        } else {
          const { data } = await apolloClient.query({
            query: gql`
              query quotePdf(
                $url: String!
                $pdfUrl: String
                $silent: Boolean
                $revision: Boolean
                $paymentId: String
                $lang: String
              ) {
                quotePdf(
                  url: $url
                  pdfUrl: $pdfUrl
                  silent: $silent
                  revision: $revision
                  paymentId: $paymentId
                  lang: $lang
                ) {
                  quotePdfUrl
                }
              }
            `,
            variables: {
              url: quoteViewUrl,
              pdfUrl: quotePdfUrl,
              silent,
              revision,
              paymentId,
              // Pass language explicitly rather than smuggling it inside the url string,
              // which gets percent-encoded into the `u` param and lost (issue #20632).
              lang: lang && lang !== 'en' ? lang : undefined
            },
            fetchPolicy: 'network-only'
          })

          if (data.quotePdf.quotePdfUrl === 'ECONNABORTED') {
            // The sync render couldn't finish inside the API's ~20s budget, so the server kicked off
            // the async task, which re-renders with a 5-min budget and writes to this exact same
            // cache key. Wait it out here rather than sending the user off to their inbox — that
            // usually lands the download in-session (#22017). The task still emails/chats the link
            // regardless, so a timeout here just falls back to the original message.
            setWaiting(true)
            abortRef.current = new AbortController()
            const landed = await waitForPdf({
              url: quotePdfUrl,
              urlExists,
              signal: abortRef.current.signal
            })
            setWaiting(false)

            if (landed) {
              await downloadPdf(quotePdfUrl, quotePdfFilename)
              return
            }

            // Gave up (or the dialog was cancelled) — tell them where the link will show up.
            const customerViewNoEmail = custViewUrl && !quoteViewUrl.includes(':')
            const message = customerViewNoEmail
              ? __(
                  "Looks like this is taking a little while... we'll send a message through the chat with a link to the PDF when it's done!"
                )
              : __("Looks like this is taking a little while... we'll email you a link to the PDF when it's done!")
            setError({
              error: 'ECONNABORTED',
              message
            })
          } else {
            await downloadPdf(data.quotePdf.quotePdfUrl, quotePdfFilename)
          }
        }
      } catch (error) {
        setError({ error })
      }
    }

    if (open) {
      fetchData()
    }

    // Never keep polling for a dialog that's gone.
    return () => abortRef.current?.abort()
  }, [open])

  function handleCancel() {
    abortRef.current?.abort()
    if (onConfirm) {
      onConfirm()
    }
  }

  async function downloadPdf(quotePdfUrl: string, fileName: string) {
    try {
      // Download pdf from bucket and save
      const { data: fileData } = await http({
        url: quotePdfUrl,
        method: 'get',
        headers: {
          Accept: 'application/pdf'
        },
        responseType: 'arraybuffer'
      })

      // With this url, download quote data to save
      // FileSaver.saveAs(new Blob([fileData], { type: 'application/pdf' }), `${fileName}`)
      // Alternative to FileSaver, make an anchor tag and click it instead, better cross browser compatibility
      const blob = new Blob([fileData], { type: 'application/pdf' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${fileName}`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
      if (onConfirm) {
        onConfirm()
      }
    } catch (error) {
      setError({ error })
      console.log('error', error)
    }
  }

  // Get our pdf url for this quoteId, rev, view, quoteUrl
  async function getPdfUrl(quote: QuoteDocument, quoteRev: string, companyId: string) {
    let quoteViewUrl: string
    let report: string | null = null
    if (custViewUrl) {
      // Avoid quoteUrl if in customer view and its given.
      quoteViewUrl = custViewUrl
    } else if (revisionQuoteUrl) {
      quoteViewUrl = revisionQuoteUrl
    } else {
      const { data } = await apolloClient.query({
        query: gql`
          query quoteUrl($id: String!, $rev: String!, $view: String!, $paymentId: String) {
            quoteUrl(id: $id, rev: $rev, view: $view, silent: true, paymentId: $paymentId) {
              url {
                _id
                report
              }
              quote
            }
          }
        `,
        variables: {
          id: quote._id,
          rev: quoteRev,
          view,
          paymentId
        }
      })

      const { quoteUrl } = data
      report = quoteUrl?.url?.report ?? null
      quoteViewUrl = quoteUrl?.url?._id ?? null
    }

    const quotePdfFilename = getTemplatedFilename({
      report,
      options: options.options,
      quote,
      view
    })
    const cacheView = lang && lang !== 'en' ? `${view}-${lang}` : view
    const quotePdfUrl = getPdfBucketUrl({
      companyId,
      quoteRev,
      quoteId: quote._id,
      url: quoteViewUrl,
      view: cacheView,
      filename: quotePdfFilename
    })
    // Note: language is passed to the quotePdf query as an explicit `lang` variable,
    // not appended to the url here — appending it caused the renderer URL to lose the
    // language (percent-encoded into `u`) and produce an English PDF (issue #20632).
    return { quotePdfUrl, quoteViewUrl, quotePdfFilename }
  }
  return (
    <Dialog open={open} {...props}>
      <DialogTitle>{__('Download PDF')}</DialogTitle>
      <DialogContent>
        <div className={classes.root}>
          <Grid container spacing={2} alignItems={'center'} direction={'column'}>
            {!error && (
              <>
                <Grid item xs={12}>
                  <Typography variant="body1" gutterBottom={true}>
                    {waiting
                      ? __("This one's taking a little longer — hang tight, we're still building your PDF.")
                      : __('Please wait while we generate your PDF.')}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Spinner />
                </Grid>
              </>
            )}
            {error && (
              <Grid item xs={12}>
                <Typography variant="body1" gutterBottom={true}>
                  {error.message ? error.message : __('Unable to generate PDF. Please try again later.')}
                </Typography>
              </Grid>
            )}
          </Grid>
        </div>
      </DialogContent>
      <DialogActions
        leftButton={
          !error && (
            <Button variant="textNoPadding" onClick={handleCancel}>
              {__('Cancel')}
            </Button>
          )
        }
      >
        {error && (
          <Button variant={'contained'} onClick={onConfirm}>
            {__('Done')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}

export default withStyles(styles)(QuotePdfDialog)
